import React, { FC, Fragment } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { GridLayoutSizes } from 'types/app';
import { useContent, useDimensions } from 'hooks';
import { getImageSrc } from 'helpers';

import { getDisplayImage, getGridImageUrl, getTitle } from '../helpers';
import { PostMenu } from '../widgets';
import { Box } from '@mui/material';

interface Props {
  variant: GridLayoutSizes;
  hovered: boolean;
}

const MINI_IMAGE_HEIGHT = 130;
const SMALL_IMAGE_HEIGHT = 200;
const MEDIUM_IMAGE_HEIGHT = 260;
const LARGE_IMAGE_HEIGHT = 350;

const styles = {
  wrapper: {
    position: 'relative',
  },
  menu: {
    opacity: 0,
    transition: 'opacity 150ms ease-in-out',
    position: 'absolute',
    top: 8,
    right: 8,
  },
  menuHover: {
    opacity: 1,
  },
  image: {
    display: 'block',
    lineHeight: 0,
  },
  mask: {
    transition: 'opacity 150ms ease-in-out',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    opacity: 0,
    backgroundImage: 'linear-gradient(180deg, #000000 0%, rgba(0,0,0,0.00) 73%)',
  },
  maskHover: {
    opacity: 0.5,
  },
  largeRadius: {
    borderRadius: '0 16px 16px 0',
  },
  otherRadius: {
    borderRadius: '16px 16px 0 0',
  },
} as const;

const ImageSection: FC<Props> = ({ variant, hovered }) => {
  const dimensions = useDimensions();
  const content = useContent();

  const containerClasses = {
    ...styles.menu,
    ...(hovered && styles.menuHover),
  };

  const maskClasses = {
    ...styles.mask,
    ...(hovered && styles.maskHover),
    ...(variant === GridLayoutSizes.Large ? styles.largeRadius : styles.otherRadius),
  };

  const imageClasses = {
    ...styles.image,
    ...(GridLayoutSizes.Large ? styles.largeRadius : styles.otherRadius),
  };

  const imageOptions = {
    height: 0,
    width: dimensions.width,
    maxWidth: dimensions.width,
  };

  let height;

  switch (variant) {
    case GridLayoutSizes.Mini:
      height = MINI_IMAGE_HEIGHT;
      imageOptions.height = MINI_IMAGE_HEIGHT;
      break;
    case GridLayoutSizes.Small:
      height = SMALL_IMAGE_HEIGHT;
      imageOptions.height = SMALL_IMAGE_HEIGHT;
      break;
    case GridLayoutSizes.Medium:
      height = MEDIUM_IMAGE_HEIGHT;
      imageOptions.height = MEDIUM_IMAGE_HEIGHT;
      break;
    case GridLayoutSizes.Large:
      height = LARGE_IMAGE_HEIGHT;
      imageOptions.height = LARGE_IMAGE_HEIGHT;
      break;
  }

  const title = getTitle(content);
  const displayImage = getDisplayImage(content);
  const imageUrl = getGridImageUrl(displayImage, imageOptions, variant);

  if (!displayImage) {
    return null;
  }

  return (
    <Fragment>
      <Box sx={styles.wrapper}>
        <RouterLink style={imageClasses} to={`/content/${content.content_id}`}>
          <img height={height} alt={title} {...getImageSrc(imageUrl)} />
          <Box style={{ height }} sx={maskClasses} />
        </RouterLink>
        <Box sx={containerClasses}>
          <PostMenu theme="dark" size="small" />
        </Box>
      </Box>
    </Fragment>
  );
};

export default ImageSection;
